import { useEffect, useState } from "react";
import { Badge, Button, Card, Checkbox, Group, Modal, Select, Stack, Switch, Table, Text, TextInput, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconPlus } from "@tabler/icons-react";
import { TAG_KEY_PATTERN, type TagSubject } from "@safiri/shared";
import { PageHeader } from "../../components/common/PageHeader";
import { QueryError } from "../../components/common/QueryError";
import { tagsApi, type TagDefinition } from "../../api/tags";
import { usePermission } from "../../auth/usePermission";
import { errorMessage } from "../../lib/axios";

const COLORS = ["gray", "blue", "teal", "green", "yellow", "orange", "red", "grape", "violet", "indigo", "cyan", "pink"];
const SUBJECT_LABEL: Record<TagSubject, string> = { driver: "Drivers", rider: "Riders" };

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

interface Draft {
  key: string;
  label: string;
  description: string;
  appliesTo: TagSubject[];
  color: string;
}

const blank: Draft = { key: "", label: "", description: "", appliesTo: ["driver"], color: "blue" };

function TagModal({ tag, opened, onClose }: { tag: TagDefinition | null; opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(blank);
  const [keyTouched, setKeyTouched] = useState(false);
  const isNew = !tag;

  // Re-seed each time the modal opens.
  useEffect(() => {
    if (!opened) return;
    setKeyTouched(false);
    setD(tag ? { key: tag.key, label: tag.label, description: tag.description ?? "", appliesTo: tag.appliesTo, color: tag.color } : blank);
  }, [opened, tag]);

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? tagsApi.create({ key: d.key, label: d.label.trim(), description: d.description.trim() || null, appliesTo: d.appliesTo, color: d.color })
        : tagsApi.update(tag!.key, { label: d.label.trim(), description: d.description.trim() || null, appliesTo: d.appliesTo, color: d.color }),
    onSuccess: () => {
      notifications.show({ color: "green", message: isNew ? "Tag created" : "Tag saved" });
      void qc.invalidateQueries({ queryKey: ["tags"] });
      void qc.invalidateQueries({ queryKey: ["tags-with-counts"] });
      onClose();
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const keyError = d.key && !TAG_KEY_PATTERN.test(d.key) ? "Lower-case letters, digits and hyphens" : null;
  const valid = d.label.trim() && d.key && !keyError && d.appliesTo.length > 0;

  return (
    <Modal opened={opened} onClose={onClose} title={isNew ? "New tag" : `Edit "${tag!.label}"`} centered>
      <Stack>
        <TextInput
          label="Name"
          placeholder="Branded Vehicle"
          value={d.label}
          onChange={(e) => {
            const label = e.currentTarget.value;
            setD((x) => ({ ...x, label, key: isNew && !keyTouched ? slugify(label) : x.key }));
          }}
          required
        />
        <TextInput
          label="Key"
          description={isNew ? "Used in filters and exports. Can't be changed later." : "Can't be changed."}
          value={d.key}
          onChange={(e) => {
            setKeyTouched(true);
            setD((x) => ({ ...x, key: e.currentTarget.value.toLowerCase() }));
          }}
          error={keyError}
          disabled={!isNew}
          required
        />
        <Textarea label="Description (optional)" value={d.description} onChange={(e) => setD((x) => ({ ...x, description: e.currentTarget.value }))} autosize minRows={2} />
        <Checkbox.Group label="Can be put on" value={d.appliesTo} onChange={(v) => setD((x) => ({ ...x, appliesTo: v as TagSubject[] }))} required>
          <Group mt={4}>
            <Checkbox value="driver" label="Drivers" />
            <Checkbox value="rider" label="Riders" />
          </Group>
        </Checkbox.Group>
        <Select label="Colour" data={COLORS} value={d.color} onChange={(v) => v && setD((x) => ({ ...x, color: v }))} renderOption={({ option }) => <Badge color={option.value} variant="light">{option.value}</Badge>} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            {isNew ? "Create tag" : "Save"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * The tag catalogue: identification tags admins can put on driver and rider
 * accounts (e.g. "Branded Vehicle"). They mark an account and change nothing
 * about it; filter and export by tag from the Drivers and Riders lists.
 */
export function TagsPage() {
  const qc = useQueryClient();
  const canManage = usePermission("tags.manage");
  const tags = useQuery({ queryKey: ["tags-with-counts"], queryFn: () => tagsApi.list(true) });
  const [editing, setEditing] = useState<TagDefinition | null>(null);
  const [open, setOpen] = useState(false);

  const archive = useMutation({
    mutationFn: (t: TagDefinition) => tagsApi.update(t.key, { archived: !t.archived }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tags"] });
      void qc.invalidateQueries({ queryKey: ["tags-with-counts"] });
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });

  return (
    <Stack>
      <PageHeader
        title="Tags"
        subtitle="Labels for identifying driver and rider accounts. Only admins see them."
        actions={
          canManage && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); setOpen(true); }}>
              New tag
            </Button>
          )
        }
      />
      {tags.error ? (
        <QueryError error={tags.error} onRetry={tags.refetch} />
      ) : (
        <Card withBorder radius="md" padding={0}>
          <Table.ScrollContainer minWidth={640}>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Tag</Table.Th>
                  <Table.Th>Key</Table.Th>
                  <Table.Th>Can be put on</Table.Th>
                  <Table.Th ta="right">Drivers</Table.Th>
                  <Table.Th ta="right">Riders</Table.Th>
                  <Table.Th>Active</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(tags.data ?? []).map((t) => (
                  <Table.Tr key={t.key} opacity={t.archived ? 0.6 : 1}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Badge variant="light" color={t.color} w="fit-content">
                          {t.label}
                        </Badge>
                        {t.description && (
                          <Text size="xs" c="dimmed">
                            {t.description}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {t.key}
                      </Text>
                    </Table.Td>
                    <Table.Td>{t.appliesTo.map((s) => SUBJECT_LABEL[s]).join(", ")}</Table.Td>
                    <Table.Td ta="right">{t.counts?.driver ?? 0}</Table.Td>
                    <Table.Td ta="right">{t.counts?.rider ?? 0}</Table.Td>
                    <Table.Td>
                      <Switch
                        size="xs"
                        checked={!t.archived}
                        disabled={!canManage}
                        onChange={() => archive.mutate(t)}
                        aria-label={t.archived ? "Restore tag" : "Archive tag"}
                      />
                    </Table.Td>
                    <Table.Td>
                      {canManage && (
                        <Button size="xs" variant="subtle" onClick={() => { setEditing(t); setOpen(true); }}>
                          Edit
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {!tags.isLoading && (tags.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text size="sm" c="dimmed" ta="center" py="md">
                        No tags yet. Create one, e.g. "Branded Vehicle".
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
      <Text size="xs" c="dimmed">
        Archived tags can't be added to accounts; accounts that already have one keep it. Put tags on an account from its
        profile page, and filter or export by tag from the Drivers and Riders lists.
      </Text>
      <TagModal tag={editing} opened={open} onClose={() => setOpen(false)} />
    </Stack>
  );
}
